import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { DateTime } from 'luxon';
import { finalize } from 'rxjs';
import { PanelHostService } from '../../../../services/panel-host.service';
import { TripInfoService } from '../../../../services/trip-info.service';
import { TripService } from '../../../../services/trip.service';
import { FlightDirection, FlightDirections, TripFlightWrite } from '../../../../models';
import { toApiDateString } from '../../../trip-finances/trip-finance-date.util';
import { ContentContainerComponent } from '../../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../../ui/panel-body/panel-body.component';

function formatTimeForInput(time: string | null | undefined): string {
  if (!time) {
    return '';
  }
  const parsed = DateTime.fromFormat(time, 'h:mm a');
  return parsed.isValid ? parsed.toFormat('HH:mm') : time;
}

function formatLocalDateForInput(date: Date): string {
  return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
}

@Component({
  selector: 'app-edit-flight-panel',
  imports: [ReactiveFormsModule, TranslocoDirective, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './edit-flight-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditFlightPanelComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripInfoService = inject(TripInfoService);
  private readonly tripService = inject(TripService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);
  private readonly fb = inject(FormBuilder);

  readonly flightDirections = FlightDirections;

  readonly form = this.fb.nonNullable.group({
    direction: this.fb.nonNullable.control<FlightDirection>(
      FlightDirections.toLocation,
      Validators.required,
    ),
    size: this.fb.nonNullable.control(1, [Validators.required, Validators.min(0)]),
    flightline: this.fb.nonNullable.control('', Validators.required),
    flightnum: this.fb.nonNullable.control('', Validators.required),
    flightfromport: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(3)]),
    flightfromdate: this.fb.nonNullable.control('', Validators.required),
    flightfromtime: this.fb.nonNullable.control('', Validators.required),
    flighttoport: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(3)]),
    flighttodate: this.fb.nonNullable.control('', Validators.required),
    flighttotime: this.fb.nonNullable.control('', Validators.required),
  });

  submitting = signal(false);
  editingFlightId: number | null = null;
  isEdit = computed(() => this.panelHost.flightPanelContext()?.action === 'edit');
  currentTrip = computed(() => this.tripService.currentTrip.value());

  constructor() {
    effect(() => {
      const panel = this.panelHost.activePanel();
      const context = this.panelHost.flightPanelContext();
      if (panel !== 'edit-flight' || !context) {
        return;
      }

      if (context.action === 'edit' && context.flight) {
        const flight = context.flight;
        this.editingFlightId = flight.id;
        this.form.reset({
          direction: flight.direction,
          size: flight.size,
          flightline: flight.flightline,
          flightnum: flight.flightnum,
          flightfromport: flight.flightfromport,
          flightfromdate: formatLocalDateForInput(flight.flightfromdate),
          flightfromtime: formatTimeForInput(flight.flightfromtime),
          flighttoport: flight.flighttoport,
          flighttodate: formatLocalDateForInput(flight.flighttodate),
          flighttotime: formatTimeForInput(flight.flighttotime),
        });
      } else {
        this.editingFlightId = null;
        this.form.reset({
          direction: FlightDirections.toLocation,
          size: 1,
          flightline: '',
          flightnum: '',
          flightfromport: '',
          flightfromdate: '',
          flightfromtime: '',
          flighttoport: '',
          flighttodate: '',
          flighttotime: '',
        });
      }
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  save(): void {
    if (this.form.invalid || this.submitting()) {
      this.form.markAllAsTouched();
      return;
    }

    const trip = this.currentTrip();
    if (!trip) {
      return;
    }

    const value = this.form.getRawValue();
    const body: TripFlightWrite = {
      direction: value.direction,
      size: value.size,
      flightline: value.flightline.trim(),
      flightnum: value.flightnum.trim(),
      flightfromport: value.flightfromport.trim().toUpperCase(),
      flightfromdate: toApiDateString(value.flightfromdate),
      flightfromtime: value.flightfromtime,
      flighttoport: value.flighttoport.trim().toUpperCase(),
      flighttodate: toApiDateString(value.flighttodate),
      flighttotime: value.flighttotime,
    };

    this.submitting.set(true);
    const request$ =
      this.isEdit() && this.editingFlightId !== null
        ? this.tripInfoService.updateFlight(trip.id, this.editingFlightId, body)
        : this.tripInfoService.createFlight(trip.id, body);

    request$.pipe(finalize(() => this.submitting.set(false))).subscribe({
      next: async () => {
        const toast = await this.toastController.create({
          message: this.transloco.translate(
            marker(this.isEdit() ? 'flights.panel.saveSuccessEdit' : 'flights.panel.saveSuccessAdd'),
          ),
          duration: 4000,
          color: 'success',
        });
        await toast.present();
        this.panelHost.dismiss();
      },
      error: async () => {
        const toast = await this.toastController.create({
          message: this.transloco.translate(marker('flights.panel.saveError')),
          duration: 4000,
          color: 'danger',
        });
        await toast.present();
      },
    });
  }
}
